import React, { useEffect, useState } from "react";
import { Webhook, Copy, CheckCheck, Eye, EyeOff, Trash2, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { useAuth, adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";

/**
 * ResendWebhookSection — Instellingen → Integraties → E-mail & Webhooks → Resend
 *
 * Veilige UI voor het beheren van de Resend webhook signing secret.
 *
 * Beveiliging:
 * - Secret komt nooit volledig terug van backend, alleen masked preview
 * - Owner/Admin only — andere rollen krijgen 403 vanuit backend
 * - Input wordt na opslaan gewist uit state
 */
const STATUS_BADGE_OK = "bg-emerald-50 text-emerald-800 border-emerald-200";
const STATUS_BADGE_WARN = "bg-amber-50 text-amber-800 border-amber-200";
const STATUS_BADGE_BAD = "bg-red-50 text-red-700 border-red-200";

function StatusPill({ ok, warn, label, testid }) {
  const cls = ok ? STATUS_BADGE_OK : warn ? STATUS_BADGE_WARN : STATUS_BADGE_BAD;
  const Icon = ok ? ShieldCheck : ShieldAlert;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function ResendWebhookSection() {
  const { user } = useAuth();
  const isPrivileged = ["owner", "admin"].includes(user?.role);

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [secretInput, setSecretInput] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!isPrivileged) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await adminApi.get("/admin/integrations/resend-webhook");
        if (!cancelled) setState(r.data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPrivileged, refreshCounter]);

  if (!isPrivileged) {
    return (
      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
          <Webhook className="w-5 h-5" /> Resend Webhook Integratie
        </h2>
        <p className="text-sm text-amber-700 mt-3" data-testid="resend-webhook-no-access">
          Alleen Owner of Admin mag de webhook-configuratie beheren.
        </p>
      </div>
    );
  }

  const handleCopyUrl = async () => {
    const url = state?.endpoint_url || "";
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Webhook URL gekopieerd");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopiëren mislukt — selecteer en kopieer handmatig");
    }
  };

  const handleSaveSecret = async () => {
    const value = (secretInput || "").trim();
    if (!value) {
      toast.error("Geen secret ingevuld");
      return;
    }
    if (value.length < 10) {
      toast.error("Secret te kort — controleer of u alles uit Resend heeft gekopieerd");
      return;
    }
    setSaving(true);
    try {
      await adminApi.put("/admin/integrations/resend-webhook/secret", { secret: value });
      toast.success("Webhook secret opgeslagen (versleuteld)");
      setSecretInput("");
      setShowSecret(false);
      setRefreshCounter((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleClearSecret = async () => {
    if (!window.confirm("Webhook secret uit database wissen?\n\nValt terug op env-var indien aanwezig.")) return;
    setClearing(true);
    try {
      await adminApi.delete("/admin/integrations/resend-webhook/secret");
      toast.success("Secret gewist (valt terug op env-var)");
      setRefreshCounter((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClearing(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await adminApi.post("/admin/integrations/resend-webhook/test", {});
      if (r.data.ok) {
        toast.success(r.data.message || "Test geslaagd");
      } else {
        toast.error(r.data.message || "Test mislukt");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-6 rounded-lg" data-testid="resend-webhook-loading">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Webhook-configuratie laden…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 p-6 rounded-lg">
        <p className="text-sm text-red-700" data-testid="resend-webhook-load-error">{error}</p>
      </div>
    );
  }

  const secret = state?.secret || {};
  const stats = state?.stats || {};
  const sourceLabel = secret.source === "db_override"
    ? "Database (versleuteld)"
    : secret.source === "env"
      ? "Environment variable"
      : "Niet ingesteld";

  return (
    <div className="space-y-5" data-testid="resend-webhook-section">
      {/* Header */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
            <Webhook className="w-5 h-5" /> Resend Webhook Integratie
          </h2>
          {secret.configured ? (
            <StatusPill ok label="Secret ingesteld" testid="resend-webhook-status-ok" />
          ) : (
            <StatusPill warn={false} label="Niet ingesteld" testid="resend-webhook-status-missing" />
          )}
        </div>
        <p className="text-xs text-slate-600 mt-2">
          Resend stuurt events (afgeleverd, geopend, bounce, klacht) naar onze backend. Met
          deze signing secret verifiëren we dat een binnenkomende webhook echt van Resend
          komt.
        </p>
      </div>

      {/* Endpoint URL */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <h3 className="font-chivo font-bold text-[#0A192F] mb-3">1. Webhook endpoint URL</h3>
        <p className="text-xs text-slate-600 mb-3">
          Plak deze URL in <span className="font-mono text-slate-800">Resend → Webhooks → Add Endpoint</span> en selecteer alle <span className="font-mono text-slate-800">email.*</span> events.
        </p>
        <div className="flex items-center gap-2">
          <Input
            value={state?.endpoint_url || ""}
            readOnly
            className="font-mono text-xs"
            data-testid="resend-webhook-endpoint-url"
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleCopyUrl}
            className="shrink-0"
            data-testid="resend-webhook-copy-url"
          >
            {copied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            <span className="ml-1">{copied ? "Gekopieerd" : "Kopieer"}</span>
          </Button>
        </div>
      </div>

      {/* Secret */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <h3 className="font-chivo font-bold text-[#0A192F] mb-3">2. Signing secret</h3>

        {secret.configured && (
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] text-slate-500 uppercase">Huidig secret</div>
                <div className="font-mono text-sm text-slate-900 truncate" data-testid="resend-webhook-secret-preview">
                  {secret.preview}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Bron: {sourceLabel} · {secret.length} tekens
                </div>
              </div>
              {secret.source === "db_override" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearSecret}
                  disabled={clearing}
                  className="text-red-600 shrink-0"
                  data-testid="resend-webhook-clear-secret"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  {clearing ? "Wissen…" : "Wissen"}
                </Button>
              )}
            </div>
          </div>
        )}

        <Label className="text-xs uppercase text-slate-500">
          {secret.configured ? "Vervang secret" : "Nieuw secret"}
        </Label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            type={showSecret ? "text" : "password"}
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
            placeholder="whsec_…"
            className="font-mono text-xs"
            data-testid="resend-webhook-secret-input"
            autoComplete="off"
            spellCheck={false}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setShowSecret((s) => !s)}
            data-testid="resend-webhook-toggle-show"
          >
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            type="button"
            onClick={handleSaveSecret}
            disabled={saving || !secretInput.trim()}
            data-testid="resend-webhook-save-secret"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 leading-snug" data-testid="resend-webhook-privacy-note">
          Wordt versleuteld opgeslagen in de database. Volledige waarde wordt na opslaan
          nooit teruggetoond. Alleen Owner/Admin mag dit veld beheren.
        </p>
      </div>

      {/* Test button */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-chivo font-bold text-[#0A192F]">3. Configuratie testen</h3>
            <p className="text-xs text-slate-600 mt-1">
              Round-trip self-test van de huidige secret (sign + verify in proces).
            </p>
          </div>
          <Button
            type="button"
            onClick={handleTest}
            disabled={testing || !secret.configured}
            data-testid="resend-webhook-test-button"
          >
            {testing ? "Testen…" : "Webhook configuratie testen"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border border-gray-200 p-6 rounded-lg" data-testid="resend-webhook-stats">
        <h3 className="font-chivo font-bold text-[#0A192F] mb-3">4. Webhook activiteit</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Totaal ontvangen</div>
            <div className="font-mono text-lg font-bold text-slate-900" data-testid="resend-webhook-stat-total">
              {stats.count_received ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Succesvol verwerkt</div>
            <div className="font-mono text-lg font-bold text-emerald-700" data-testid="resend-webhook-stat-success">
              {stats.count_success ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Geweigerd (sig)</div>
            <div
              className={`font-mono text-lg font-bold ${
                (stats.count_invalid_signature ?? 0) > 0 ? "text-red-700" : "text-slate-900"
              }`}
              data-testid="resend-webhook-stat-rejected"
            >
              {stats.count_invalid_signature ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Onverified (preview)</div>
            <div className="font-mono text-lg font-bold text-slate-900" data-testid="resend-webhook-stat-unverified">
              {stats.count_unverified ?? 0}
            </div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div className="flex justify-between border-b border-slate-100 py-1">
            <span className="text-slate-500">Laatste ontvangen</span>
            <span className="text-slate-900 font-mono" data-testid="resend-webhook-last-received">{fmtTs(stats.last_received_at)}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 py-1">
            <span className="text-slate-500">Laatste succesvol</span>
            <span className="text-slate-900 font-mono" data-testid="resend-webhook-last-success">{fmtTs(stats.last_success_at)}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 py-1">
            <span className="text-slate-500">Laatste invalid signature</span>
            <span className="text-slate-900 font-mono" data-testid="resend-webhook-last-invalid">{fmtTs(stats.last_invalid_at)}</span>
          </div>
          <div className="flex justify-between border-b border-slate-100 py-1">
            <span className="text-slate-500">Laatste foutdetail</span>
            <span className="text-slate-900 font-mono truncate max-w-[300px]" title={stats.last_failure_detail || ""}>
              {stats.last_failure_detail || "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
